'use strict';

import { cn_niche } from '../model/cn_niche';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_svg_map } from './cn_svg_map';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_add, cn_box, cn_dot, cn_middle, cn_mul, cn_normalize, cn_size, cn_sub } from '../utils/cn_utilities';
import { cn_pastille } from './cn_pastille';
import { cn_type_pastille } from './cn_type_pastille';
import { cn_edit_box } from './cn_edit_box';
import { cn_number_input, cn_number_list_input } from './cn_inputs';

export class cn_niche_handler extends cn_edition_handler {
    //***********************************************************************************
    /**
     * Constructor
     * @param {Array<cn_niche>} niches
     * @param {cn_svg_map} map
     * @param {boolean} creation
     */
    constructor(niches, map, creation = false) {
        super(niches, map);
        const obj = this;

        this._map = map;
        this._scene = this._map._scene;
        this._transaction_manager = this._map._building.transaction_manager;

        this._niches = niches;
        this._niche = (niches.length == 1) ? niches[0] : null;

        //** mouse flags */
        this._mouseover = -1;
        this._mouseover_niche = false;

        this._start = [true, false, false, true];
        this._inside = [false, true, false, true];

        this._measures = [];

        this._grabbed = false;

        this._draw_measures = true;

        this._niche_measures = [
            { selected: false, position: [0, 0, 0], reverse: false },
            { selected: false, position: [0, 0, 0], reverse: true }];
        this._niche_dimensions = { selected: false, position: [0, 0, 0] };

        // @type{selected: boolean, reverse: boolean, position; number[]}
        this._mouseover_measure = null;

        this._edit_box = null;
        this._build_edit_box();

        this.transaction_increment = 0;

        this._need_find_ambiguous_niches = true;
        this._ambiguous_niches = [];
        this._find_ambiguous_niches();
    }

    _find_ambiguous_niches() {
        if (!this._need_find_ambiguous_niches) return;
        this._ambiguous_niches = [];
        this._ambiguous_box_mouseover = -1;
        if (this._niche) {
            let x0 = this._niche.wall_position;
            let x1 = x0 + this._niche.width;
            if (this._niche.wall) {
                this._ambiguous_niches = this._niche.wall.niches.filter(op => op.valid && op != this._niche && op.wall_position < x1 && op.wall_position + op.width > x0);
                if (this._ambiguous_niches.length)
                    this._ambiguous_niches.push(this._niche);
            }
        }
        this._need_find_ambiguous_niches = false;
    }

    _build_edit_box() {

        //*** Edit box for mass selection */
        const edit_box = new cn_edit_box(this, this._niches, this._readOnly);
        this._edit_box = edit_box;
        this._handlers = [edit_box];

        //*** axis pastille */
        if (!this._readOnly) {
            //*** Dimensions pastille */
            const dimensions_pastille = new cn_pastille([0, 0], 'tape_measure.svg');
            edit_box.add_pastille(dimensions_pastille);
            dimensions_pastille.svg_class = 'pastille_background white';
            dimensions_pastille.title = 'Dimensions';
            dimensions_pastille.clicked = () => {
                this._select_niche_dimensions();
            }

            //*** Type pastille */
            const type_pastille = new cn_type_pastille(this._niches, 'element_type', () => {return this._map._building.get_niche_types();}, 'Type de niche', this._map);
            type_pastille.title = 'Modifier le modèle de la niche';
            edit_box.add_pastille(type_pastille);
            type_pastille.on("clicked", () => {
                type_pastille.checkbox_status = false;
                this._niches.forEach(niche => niche["old_niche_type"] = niche.element_type);
            });

            edit_box.add_lock_pastille(this._transaction_manager);
        }

    }

    _select_niche_dimensions() {

        const input_list = [];

        const input_width = new cn_number_input('Largeur', this._niches[0].width, 'm', 2, 0.1, 10);
        const variable_width = this._niches.some(s => s.width != this._niches[0].width);
        input_width.checkbox_label = (variable_width) ? "Variable" : "";
        input_width.checkbox_status = variable_width;
        input_list.push(input_width);

        const input_height = new cn_number_input('Hauteur', this._niches[0].height, 'm', 2, 0.1, 10);
        const variable_height = this._niches.some(s => s.height != this._niches[0].height);
        input_height.checkbox_label = (variable_height) ? "Variable" : "";
        input_height.checkbox_status = variable_height;
        input_list.push(input_height);

        const input_z = new cn_number_input('Décalage', this._niches[0].z, 'm', 2, 0, 10);
        const variable_z = this._niches.some(s => s.z != this._niches[0].z);
        input_z.checkbox_label = (variable_z) ? "Variable" : "";
        input_z.checkbox_status = variable_z;
        input_list.push(input_z);

        const input_depth = new cn_number_input('Profondeur', this._niches[0].depth, 'm', 2, 0, 10);
        const variable_depth = this._niches.some(s => s.depth != this._niches[0].depth);
        input_depth.checkbox_label = (variable_depth) ? "Variable" : "";
        input_depth.checkbox_status = variable_depth ;
        input_list.push(input_depth);

        const input = new cn_number_list_input('Dimensions', input_list);
        input.callback = () => {
            this._transaction_manager.push_transaction('Dimensions de niches');
            this._niches.forEach(niche => {
                this._transaction_manager.push_item_set(niche, ['width', 'height', 'z', 'depth']);
                if (!input_width.checkbox_status)
                    niche.width = input_width.value;

                if (!input_height.checkbox_status)
                    niche.height = input_height.value;

                if (!input_z.checkbox_status)
                    niche.z = input_z.value;

                if (!input_depth.checkbox_status)
                    niche.depth = input_depth.value;

            });
            this._map.refresh();
        };
        this._map.call('number_list_input', input);
    }

    //***********************************************************************************
    //**** Force one measure
    //***********************************************************************************
    set_current_measure(v) {
        if (!this._mouseover_measure) return false;

        var delta = v - this._mouseover_measure.position[2];
        if (this._mouseover_measure.reverse) delta = -delta;
        var prev_pos = this._niche.wall_position;
        this._niche.wall_position += delta;
        if (!this._niche.check_wall()) {
            this._niche.wall_position = prev_pos;
            return false;
        }

        this._niche.wall_position = prev_pos;
        this._transaction_manager.push_transaction('Positionnement d\'ouvrant', this._niche.ID);
        this._transaction_manager.push_item_set(this._niche, 'position', () => {
            this._niche.update_wall_position();
            this._niche.wall.update_niches();
        });
        this._niche.wall_position += delta;
        this._niche.update_wall_position();
        this._niche.wall.update_niches();
        this._scene.update_space_numerotation();
        this._map.refresh();
        this.call('change');
        return true;
    }

    //***********************************************************************************
    //**** Draws
    //***********************************************************************************
    draw(camera, draw_measures = true) {

        if (this._niche == null) return super.draw(camera);

        if (this._edit_box.duplication_mode())
            return this._edit_box.draw(camera);

        var html = '';

        var measure_niche = this._niche;
        var wall = measure_niche.wall;
        this._draw_measures = draw_measures;

        let html_measures = measure_niche.draw_measures(camera, [], this._niche_dimensions, this._niche_measures);

        //*** Draw wall locks */
        this._niche.get_candidate_locks().forEach((v, vindex) => {
            html += camera.draw_lock(v.position, v == this._niche.wall_lock_vertex, this._mouseover == 100 + vindex);
        });

        //*** draw niche */
        if (this._mouseover_niche)
            html += this._niche.draw(camera, ['mouseover']);
        else
            html += this._niche.draw(camera, ['selected']);

        //*** draw ambiguities */
        this._find_ambiguous_niches();
        this._ambiguous_boxes = [];
        if (this._ambiguous_niches.length) {

            let radius = this._niche.width * camera.world_to_screen_scale;
            if (radius < camera.thumb_size) radius = camera.thumb_size;

            var c = cn_add(cn_middle(wall.bounds.pmin, wall.bounds.pmax), cn_mul(wall.bounds.direction, this._niche.wall_position + 0.5 * this._niche.width));
            let center = camera.world_to_screen(c);

            let box = new cn_box();
            this._ambiguous_niches.forEach(op => {
                let z0 = op.compute_offset() + op.get_z();
                box.enlarge_point([op.wall_position, z0]);
                box.enlarge_point([op.wall_position + op.width, z0 + op.height]);
                box.enlarge_distance(0.2);
            });
            let screen_size = 100;
            let scale = Math.max(box.size[0] / screen_size, box.size[1] / screen_size);
            let c0 = cn_sub([center[0] + radius, center[1] - radius], [0.2 * box.size[0] / scale, 0.2 * box.size[1] / scale]);
            html += `<rect x="${c0[0]}" y="${c0[1]}" width="${box.size[0] / scale}" height="${box.size[1] / scale}" fill="white" stroke="black" />`;
            console.log("Ambiguous");
            this._ambiguous_niches.forEach((op, idx) => {
                let z0 = op.compute_offset() + op.get_z();
                let p = cn_sub([op.wall_position, z0], box.posmin);
                //html += `<rect x="${c0[0] + p[0] / scale}" y="${c0[1] + (box.size[1] - p[1] - op.height) / scale} " width="${op.width / scale} " height="${op.height / scale} " fill="${op == this._niche ? "red" : "grey"} " stroke="black" />`;
                let rect = [c0[0] + p[0] / scale, c0[1] + (box.size[1] - p[1] - op.height) / scale, op.width / scale, op.height / scale];
                html += `<g transform="translate(${rect[0]},${rect[1]})">`;
                html += op.get_niche_type().draw_svg_icon(rect[2], rect[3]);
                if (op == this._niche)
                    html += `<rect x="0" y="0" width="${rect[2]}" height="${rect[3]}" fill="none" stroke="red" stroke-width="2" />`;
                if (idx == this._ambiguous_box_mouseover)
                    html += `<rect x="0" y="0" width="${rect[2]}" height="${rect[3]}" fill="none" stroke="yellow" stroke-width="2" />`;
                html += `</g>`;
                let bx = new cn_box();
                bx.enlarge_point([rect[0], rect[1]]);
                bx.enlarge_point([rect[0] + rect[2], rect[1] + rect[3]]);
                this._ambiguous_boxes.push(bx);
            });
        }

        //*** rec */
        html += super.draw(camera);
        html += html_measures;

        return html;
    }

    //***********************************************************************************
    //**** clear move effects
    //***********************************************************************************
    clear_move() {
        this._mouseover_niche = false;
        this._mouseover = -1;
        this._mouseover_measure = null;
        this._ambiguous_box_mouseover = -1;
        super.clear_move();
    }

    /**
     * Manage a click. Only after a grab that returned true, and no drag. To return 'true' if click had an effect.
     * @param {cn_mouse_event} mouse_event
     * @returns {boolean}
     */
    click(mouse_event) {

        if (this._edit_box.duplication_mode())
            return this._edit_box.click(mouse_event);

        //*** Click on edit box */
        if (super.click(mouse_event)) return true;

        if (this._map.get_readonly()) {
            return false;
        }

        //*** Click on ambiguous window */
        if (this._ambiguous_box_mouseover >= 0) {
            let niche = this._ambiguous_niches[this._ambiguous_box_mouseover];
            if (niche && niche != this._niche) {
                this._niche = niche;
                this._niches = [niche];
                this._need_find_ambiguous_niches = true;
                this._build_edit_box();
                return true;
            }
        }

        //*** click on measures */
        this.move(mouse_event);
        if (this._mouseover_measure) {
            if (this._mouseover_measure == this._niche_dimensions) {
                this._select_niche_dimensions();
                return true;
            }
            const input = new cn_number_input('Modifier la distance', this._mouseover_measure.position[2]);
            input.callback = () => {
                this.set_current_measure(input.value);
            };
            input.decimals = 3;
            input.unit = 'm';
            input.min_value = 0;
            this.call('number_input', input);
            this._need_find_ambiguous_niches = true;
            return true;
        }

        //*** Wall lock was clicked */
        if (this._mouseover >= 100) {
            this._transaction_manager.push_transaction('Lien d\'ouvrant');
            this._transaction_manager.push_item_set(this._niche, ['wall_lock_vertex', 'wall_position', 'position']);
            this._niche.wall_lock_vertex = this._niche.get_candidate_locks()[this._mouseover - 100];
            this._niche.update_wall_position();
        }

        return false;
    }

    grab(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.grab(mouse_event);

        this._grabbed = false;
        if (super.grab(mouse_event)) return true;

        //***Grab resize arrows ?  */
        if (this._mouseover == 20 || this._mouseover == 21) {
            this._grabbed = true;
            return true;
        }

        this._grabbed = (this._niche &&  this._niche.contains(mouse_event.mouse_world,mouse_event.camera.snap_world_distance));
        this.transaction_increment = (this.transaction_increment + 1) % 100;
        return this.move(mouse_event);
    }

    drag(ev) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.drag(ev);

        if (!this._grabbed) {
            if (super.drag(ev)) return true;
            return false;
        }

        var niche = this._niche;
        if (niche.locked) return false;

        var old_position = niche.wall_position;

        var old_wall = niche.wall;
        var new_wall = this._scene.find_wall(ev.mouse_world);
        if (new_wall && new_wall != old_wall)
            niche.wall = new_wall;
        else
            new_wall = old_wall;
        let old_wall_side = niche.wall_side;
        let new_wall_side = new_wall.compute_side(ev.mouse_world);
        niche.wall_side = new_wall_side;
        let x = new_wall.optimize_position(niche,ev.mouse_world, ev.camera.snap_world_distance);
        if (!x) {
            niche.wall_side = old_wall_side;
            niche.wall = old_wall;
        }
        if (x) {
            var new_position = x;
            niche.wall_position = old_position;
            niche.wall_side = new_wall_side;
            niche.wall = old_wall;
            this._transaction_manager.push_transaction('Déplacement de niche', niche.ID + this.transaction_increment, () => {
                niche.wall.update_niches();
            });
            this._transaction_manager.push_item_set(niche, 'wall_position');
            niche.wall_position = new_position;
            niche.set_wall(new_wall, this._transaction_manager);
            this._niche.update_wall_position();
            niche.wall.update_niches();
            this.call('change');
            this._need_find_ambiguous_niches = true;
        }
        return true;
    }

    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    move(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.move(mouse_event);

        if (super.move(mouse_event)) return true;

        //*** check measures */
        if (this._find_measure(mouse_event)) return true;

        //*** check abiguities */
        this._ambiguous_box_mouseover = this._ambiguous_boxes.findIndex(b => b.contains_point(mouse_event.mouse_screen));
        if (this._ambiguous_box_mouseover >= 0)
            return true;

        //*** check niche for drag */
        if (this._niche && this._niche.contains(mouse_event.mouse_world,mouse_event.camera.snap_world_distance)) {
            this._mouseover_niche = true;
            return true;
        }

        if (!this._niche || !this._niche.valid)
            return false;

        //*** check wall lock */
        this._niche.get_candidate_locks().forEach((v, vindex) => {
            if (v && mouse_event.camera.on_vertex(mouse_event.mouse_world, v.position)) {
                this._mouseover = 100 + vindex;
            }
        });
        if (this._mouseover >= 0) return true;

        return false;
    }

    _find_measure(ev) {
        if (!this._draw_measures) return false;
        if (!this._niche || !this._niche.valid)
            return false;
        this._niche_measures.forEach(m => m.selected = false);
        this._niche_dimensions.selected = false;
        this._mouseover_measure = [this._niche_dimensions, ...this._niche_measures].find(m => Math.abs(m.position[0] - ev.mouse_screen[0]) <= 30 && Math.abs(m.position[1] - ev.mouse_screen[1]) <= 10);
        if (this._mouseover_measure) {
            this._mouseover_measure.selected = true;
            return true;
        }
        return false;
    }
}

